'use client'

import { ArrowBigUp, ArrowBigDown, MessageSquare, Share2, ArrowLeft, Trash2, Loader2 } from 'lucide-react';
import { isStaffRole } from '@/lib/auth/roles';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { settings } from '../../../../../config/settings';
import { apiGetMe } from '../../../../helpers/auth';
import RoleBadge from '../../../../components/RoleBadge';
import { useTranslations } from 'next-intl';

export default function ForumPostPage({ params }) {
  const t = useTranslations('Auto.ForumsCategorySlug');
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingThread, setDeletingThread] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState(new Set());
  const router = useRouter();

  useEffect(() => {
    apiGetMe()
      .then((data) => setMe(data?.user ?? null))
      .catch(() => setMe(null));
  }, []);

  useEffect(() => {
    loadPost();
  }, [params.category, params.slug]);

  async function loadPost() {
    setLoading(true);
    try {
      const fullSlug = `${params.category}/${params.slug}`;
      console.log('Frontend: Loading post with slug:', fullSlug);
      console.log('Frontend: params.category:', params.category, 'params.slug:', params.slug);

      const res = await fetch(`/api/forums/posts/${encodeURIComponent(fullSlug)}`);
      const data = await res.json();

      console.log('Frontend: API response status:', res.status);
      console.log('Frontend: API response data:', data);

      if (res.ok) {
        setPost(data.post);
        setReplies(data.replies);
      } else if (res.status === 404) {
        console.log('Frontend: Post not found, redirecting to forums');
        router.push('/forums');
      }
    } catch (error) {
      console.error('Failed to load post:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleVote(targetId, voteType, targetType = 'post') {
    if (!me) return;

    try {
      const res = await fetch('/api/forums/vote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          target_type: targetType,
          target_id: targetId,
          vote_type: voteType
        })
      });

      if (res.ok) {
        const voteData = await res.json();
        if (targetType === 'post') {
          setPost(prev => ({ ...prev, ...voteData, userVote: voteData.userVote }));
        } else {
          updateReplyVotes(replies, targetId, voteData);
        }
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  }

  function updateReplyVotes(replyList, targetId, voteData) {
    return replyList.map(reply => {
      if (reply.id === targetId) {
        return { ...reply, ...voteData, userVote: voteData.userVote };
      }
      if (reply.replies) {
        return { ...reply, replies: updateReplyVotes(reply.replies, targetId, voteData) };
      }
      return reply;
    });
  }

  async function handleReply(parentReply = null) {
    if (!me || !replyContent.trim()) return;

    try {
      const res = await fetch('/api/forums/replies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          post_id: post.id,
          parent_reply_id: parentReply?.id || null,
          content: replyContent
        })
      });

      if (res.ok) {
        const data = await res.json();
        setReplyContent('');
        setReplyModalOpen(false);
        setReplyingTo(null);
        
        // Refresh the post to get updated reply count
        loadPost();
      }
    } catch (error) {
      console.error('Failed to post reply:', error);
    }
  }

  function handleShare() {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    // You could add a toast notification here
  }

  async function handleDeleteThread() {
    if (!isStaffRole(me?.access_role ?? me?.role)) return;

    setDeletingThread(true);
    try {
      const fullSlug = `${params.category}/${params.slug}`;
      const res = await fetch(`/api/forums/posts/${encodeURIComponent(fullSlug)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (res.ok) {
        setDeleteModalOpen(false);
        router.push('/forums');
        router.refresh();
      }
    } catch (error) {
      console.error('Failed to delete thread:', error);
    } finally {
      setDeletingThread(false);
    }
  }

  function formatTimeAgo(dateString: string) {
    const date = new Date(dateString);
    const diffMs: number = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  }

  function getDisplayName(user) {
    return user.display_name || user.username;
  }


  function toggleReplies(replyId) {
    const newExpanded = new Set(expandedReplies);
    if (newExpanded.has(replyId)) {
      newExpanded.delete(replyId);
    } else {
      newExpanded.add(replyId);
    }
    setExpandedReplies(newExpanded);
  }

  function renderReplies(replyList, depth = 0) {
    return replyList.map((reply) => {
      const hasReplies = reply.replies && reply.replies.length > 0;
      const showReplies = expandedReplies.has(reply.id) || depth < 2; // Show first 2 levels by default

      return (
        <div key={reply.id} style={{ marginLeft: depth * 20 }}>
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div className="flex gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={reply.avatar_path || settings.auth?.default_avatar_path} />
                    <AvatarFallback>{getDisplayName(reply)[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <Link href={`/users/${reply.username}`} className="no-underline">
                        <span className="text-sm font-semibold hover:underline cursor-pointer">
                          {getDisplayName(reply)}
                        </span>
                      </Link>
                      <RoleBadge role={reply.role} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatTimeAgo(reply.created_at)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Button
                      variant={reply.userVote === 'up' ? 'default' : 'ghost'}
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleVote(reply.id, 'up', 'reply')}
                      disabled={!me}
                    >
                      <ArrowBigUp className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs font-medium w-6 text-center">
                      {reply.score}
                    </span>
                    <Button
                      variant={reply.userVote === 'down' ? 'default' : 'ghost'}
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleVote(reply.id, 'down', 'reply')}
                      disabled={!me}
                    >
                      <ArrowBigDown className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setReplyingTo(reply);
                      setReplyModalOpen(true);
                    }}
                    disabled={!me}
                  >
                    <MessageSquare className="mr-1 h-3.5 w-3.5" />
                    {t('reply')}
                  </Button>
                </div>
              </div>

              <p className="text-sm whitespace-pre-wrap">
                {reply.content}
              </p>

              {hasReplies && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => toggleReplies(reply.id)}
                >
                  {showReplies ? t('hide') : t('show')} {reply.replies.length} {reply.replies.length === 1 ? 'reply' : 'replies'}
                </Button>
              )}
            </CardContent>
          </Card>

          {showReplies && hasReplies && renderReplies(reply.replies, depth + 1)}
        </div>
      );
    });
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 mt-32 mb-16">
        <div className="flex flex-col gap-8">
          <div className="h-10 w-48 bg-muted animate-pulse rounded" />
          <Card>
            <CardContent className="p-8">
              <div className="space-y-4">
                <div className="h-8 w-3/4 bg-muted animate-pulse rounded" />
                <div className="h-5 w-1/2 bg-muted animate-pulse rounded" />
                <div className="h-24 bg-muted animate-pulse rounded" />
                <div className="flex gap-4">
                  <div className="h-9 w-20 bg-muted animate-pulse rounded" />
                  <div className="h-9 w-20 bg-muted animate-pulse rounded" />
                  <div className="h-9 w-20 bg-muted animate-pulse rounded" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-6 mt-32 mb-16">
        <p>{t('postNotFound')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 mt-32 mb-16">
      <div className="flex flex-col gap-8">
        {/* Back Navigation */}
        <Button
          variant="ghost"
          className="w-fit"
          onClick={() => router.back()}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('backToForums')}
        </Button>

        {/* Main Post */}
        <Card>
          <CardContent className="p-8">
          <div className="flex justify-between items-start mb-6">
            <div className="flex gap-3">
              <Avatar className="h-12 w-12">
                <AvatarImage src={post.avatar_path || settings.auth?.default_avatar_path} />
                <AvatarFallback>{getDisplayName(post)[0]}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Link href={`/users/${post.username}`} className="no-underline">
                    <span className="text-lg font-semibold hover:underline cursor-pointer">
                      {getDisplayName(post)}
                    </span>
                  </Link>
                  <RoleBadge role={post.role} />
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatTimeAgo(post.created_at)} • {post.category}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Button
                  variant={post.userVote === 'up' ? 'default' : 'ghost'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleVote(post.id, 'up')}
                  disabled={!me}
                >
                  <ArrowBigUp className="h-5 w-5" />
                </Button>
                <span className="font-semibold w-12 text-center">
                  {post.score}
                </span>
                <Button
                  variant={post.userVote === 'down' ? 'default' : 'ghost'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleVote(post.id, 'down')}
                  disabled={!me}
                >
                  <ArrowBigDown className="h-5 w-5" />
                </Button>
              </div>

              <Separator orientation="vertical" className="h-8" />

              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleShare}
              >
                <Share2 className="h-5 w-5" />
              </Button>

              {isStaffRole(me?.access_role ?? me?.role) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>

          <h1 className="text-3xl font-bold mb-6">
            {post.title}
          </h1>

          <div 
            className="text-base leading-relaxed mb-6"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          <Separator className="my-6" />

          <Button
            onClick={() => setReplyModalOpen(true)}
            disabled={!me}
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            {t('reply')}
          </Button>
          </CardContent>
        </Card>

        {/* Replies Section */}
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold">
            {post.reply_count} {post.reply_count === 1 ? t('reply') : t('replies')}
          </h2>

          {replies.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">
                  No replies yet. {me ? t('beTheFirstTo') : t('signInToReply')}
                </p>
              </CardContent>
            </Card>
          ) : (
            renderReplies(replies)
          )}
        </div>
      </div>

      {/* Reply Modal */}
      <Dialog open={replyModalOpen} onOpenChange={(open) => {
        if (!open) {
          setReplyModalOpen(false);
          setReplyingTo(null);
          setReplyContent('');
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{replyingTo ? `Reply to ${getDisplayName(replyingTo)}` : t('replyToPost')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {replyingTo && (
              <Card>
                <CardContent className="p-3">
                  <p className="text-sm text-muted-foreground">
                    Replying to: {replyingTo.content.substring(0, 100)}...
                  </p>
                </CardContent>
              </Card>
            )}
            
            <Textarea
              placeholder={t('writeYourReply')}
              rows={6}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReplyModalOpen(false);
                setReplyingTo(null);
                setReplyContent('');
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => handleReply(replyingTo)}
              disabled={!replyContent.trim() || !me}
            >
              {t('postReply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModalOpen} onOpenChange={(open) => {
        if (!open && !deletingThread) setDeleteModalOpen(false);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deleteThread')}</DialogTitle>
            <DialogDescription>
              {t('areYouSureYou')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deletingThread}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteThread}
              disabled={deletingThread}
            >
              {deletingThread && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Trash2 className="mr-2 h-4 w-4" />
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
